// Radha — multi-sheet Excel export of the CRM book. Ported mechanism from the
// Nikunj CRM's workbook export (the mechanism only; columns are Rasvyn's own).
//
// SheetJS is used write-only (XLSX.utils.* + XLSX.write). The advisories on the
// npm build (prototype pollution / ReDoS) are in the *parse* path, which we never
// call. It is dynamic-imported so it lands in its own chunk, off the main bundle.
import type { CrmDeal } from "../hooks/useBatchModules";
import { adjustedProb, type StageRates } from "../hooks/useCrmDirect";

const num = (v: string | number | null) => {
  const n = typeof v === "string" ? Number(v) : v;
  return n == null || Number.isNaN(n) ? null : n;
};
const pct = (p: number) => Math.round(p * 100) / 100;

export async function exportCrmWorkbook(deals: CrmDeal[], rates?: StageRates) {
  const XLSX = await import("xlsx");

  const dealRows = deals.map((d) => ({
    Deal: d.deal_name,
    Customer: d.customer_name,
    Stage: d.stage,
    Amount: num(d.amount),
    "Win probability": pct(Number(d.win_probability)),
    "Weighted value": num(d.weighted_value),
    "Adjusted win %": pct(adjustedProb(d, rates)),
    Owner: d.owner_name ?? "",
    "Expected close": d.expected_close ?? "",
    "Last activity": d.last_activity_at ? d.last_activity_at.slice(0, 10) : "",
    "Next step": d.next_step ?? "",
    "Loss reason": d.loss_reason ?? "",
    Competitor: d.competitor ?? "",
  }));

  const rateRows = rates
    ? [...rates.entries()].map(([stage, r]) => ({
        Stage: stage,
        Reached: r.reached,
        Decided: r.decided,
        "Win rate %": r.rate == null ? "" : Math.round(r.rate * 100),
      }))
    : [];

  const lossRows = deals
    .filter((d) => d.stage === "lost")
    .map((d) => ({
      Deal: d.deal_name,
      Customer: d.customer_name,
      Amount: num(d.amount),
      Reason: d.loss_reason ?? "",
      Competitor: d.competitor ?? "",
    }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(dealRows), "Deals");
  if (rateRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rateRows), "Stage rates");
  if (lossRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lossRows), "Losses");

  // date the filename without Date.now() being disallowed here — this is app code,
  // not a workflow script, so a real Date is fine.
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `radha-crm-${stamp}.xlsx`);
}
